/**
 * The sidebar, as data.
 *
 * One list, read by the sidebar to draw itself and by the header to work out
 * which section the current URL belongs to. A second copy of this shape is how
 * a menu entry and its route quietly stop agreeing.
 *
 * Every item names the permissions that make it reachable, and the sidebar hides
 * the ones the caller holds none of. That is **presentation** — it saves someone
 * clicking into a screen that can only tell them no. It protects nothing; the
 * server checks every request regardless.
 *
 * **Labels are keys, not text.** This is module-level data, evaluated once at
 * import; a translated string baked in here would be whatever the language was
 * at page load and would never change again. The sidebar and the header resolve
 * these against the `nav` namespace at render time, which is what makes the
 * language selector move them.
 */
import type { SvgIconComponent } from '@mui/icons-material';
import type { en } from '../i18n/resources';
import type { FrontendModule, ModuleNavKey, ModuleSectionKey } from '../modules/types';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import BadgeIcon from '@mui/icons-material/Badge';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import FolderIcon from '@mui/icons-material/Folder';
import GroupsIcon from '@mui/icons-material/Groups';
import HistoryIcon from '@mui/icons-material/History';
import HubIcon from '@mui/icons-material/Hub';
import InsertChartOutlinedIcon from '@mui/icons-material/InsertChartOutlined';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import PeopleIcon from '@mui/icons-material/People';
import RuleIcon from '@mui/icons-material/Rule';
import SendIcon from '@mui/icons-material/Send';

/**
 * The key naming an item, derived from the English catalogue.
 *
 * Deriving it rather than writing `string` is what makes a menu entry with no
 * translation a compile error instead of a raw `item.whatever` on screen.
 */
export type NavItemKey = `item.${keyof typeof en.nav.item}`;

/** The id of one of core's own sections: `overview`, `people`, … */
export type CoreSectionId = keyof typeof en.nav.section;

/** The key naming a core section's heading. Derived the same way. */
export type NavSectionKey = `section.${CoreSectionId}`;

export interface NavItem {
  /**
   * A key in the `nav` namespace — or, for a module's page, in the module's own
   * namespace (`<key>:nav.<item>`). Resolved by the sidebar, never here.
   */
  labelKey: NavItemKey | ModuleNavKey;
  /** The route it goes to. Also the prefix used to decide if it is the current one. */
  to: string;
  icon: SvgIconComponent;
  /**
   * Holding **any** of these reveals it. Empty means everyone with a session.
   *
   * Resource screens carry no permission on purpose: the tree is reachable by
   * anyone the tree has been shared with, and what they may *do* once inside
   * comes from each resource's own `capabilities`, not from this list.
   */
  anyOf?: readonly string[];
  /**
   * Matches only the exact path rather than everything beneath it.
   *
   * For a route that is a prefix of its siblings: `/files` would otherwise stay
   * highlighted while the user is on `/files/trash`.
   */
  exact?: boolean;
}

export interface NavSection {
  /** What a module's page names to land here: a core id, or one a module declares. */
  id: string;
  /** The key for the heading above the group — in `nav`, or in the declaring module's namespace. */
  titleKey: NavSectionKey | ModuleSectionKey;
  /** Lower is higher in the sidebar. Core's are hundreds, so a module can fit between them. */
  order: number;
  items: NavItem[];
}

/**
 * Core's sections. A section only some module fills — Sales, Settings — is not
 * here: the modules that use it declare it (`FrontendModule.sections`).
 */
export const NAVIGATION: readonly (NavSection & { id: CoreSectionId })[] = [
  {
    id: 'overview',
    titleKey: 'section.overview',
    order: 100,
    items: [
      { labelKey: 'item.dashboard', to: '/', icon: InsertChartOutlinedIcon, exact: true },
      { labelKey: 'item.system', to: '/system', icon: MonitorHeartIcon },
    ],
  },
  {
    id: 'content',
    titleKey: 'section.content',
    order: 300,
    items: [
      { labelKey: 'item.files', to: '/files', icon: FolderIcon },
      { labelKey: 'item.trash', to: '/trash', icon: DeleteOutlinedIcon },
    ],
  },
  {
    id: 'people',
    titleKey: 'section.people',
    order: 400,
    items: [
      { labelKey: 'item.users', to: '/users', icon: PeopleIcon, anyOf: ['user.read'] },
      { labelKey: 'item.groups', to: '/groups', icon: GroupsIcon, anyOf: ['group.read'] },
      { labelKey: 'item.roles', to: '/roles', icon: BadgeIcon, anyOf: ['role.read'] },
      {
        labelKey: 'item.permissions',
        to: '/permissions',
        icon: AssignmentIndIcon,
        anyOf: ['role.read'],
      },
    ],
  },
  {
    id: 'notifications',
    titleKey: 'section.notifications',
    order: 500,
    items: [
      {
        labelKey: 'item.channels',
        to: '/notifications/channels',
        icon: HubIcon,
        anyOf: ['notification.read'],
      },
      {
        labelKey: 'item.rules',
        to: '/notifications/rules',
        icon: RuleIcon,
        anyOf: ['notification.read'],
      },
      {
        labelKey: 'item.deliveries',
        to: '/notifications/deliveries',
        icon: SendIcon,
        anyOf: ['notification.read'],
      },
    ],
  },
  {
    id: 'history',
    titleKey: 'section.history',
    order: 600,
    items: [{ labelKey: 'item.audit', to: '/audit', icon: HistoryIcon, anyOf: ['audit.read'] }],
  },
];

/**
 * Whether `pathname` is inside `item`.
 *
 * The boundary check matters: without it `/users` would claim `/users-archive`,
 * and `/notifications/rules` would claim nothing but itself while its detail
 * routes lost the highlight.
 */
export function isCurrent(item: NavItem, pathname: string): boolean {
  if (item.exact) return pathname === item.to;
  return pathname === item.to || pathname.startsWith(`${item.to}/`);
}

/**
 * The **key** for the section the current URL sits in, for the header.
 *
 * A key rather than a label, for the same reason the table holds keys: this is
 * called during render, but the header is what has a `t` to resolve it with.
 */
export function sectionKeyFor(
  pathname: string,
  navigation: readonly NavSection[] = NAVIGATION,
): NavSection['titleKey'] | undefined {
  for (const section of navigation) {
    if (section.items.some((item) => isCurrent(item, pathname))) return section.titleKey;
  }
  return undefined;
}

/**
 * Core's menu with every module's sections and page entries merged in.
 *
 * **A section belongs to no module.** Each module declares every non-core
 * section its pages use; several may declare the same id, and they all land in
 * one section. The first declaration in `MODULES` order gives the heading and
 * the position, so a module still stands alone when the others are gone.
 *
 * Two mistakes are thrown rather than drawn wrongly, like the backend's
 * registration refusing a bad manifest: a module re-declaring one of core's
 * sections, and a page naming a section neither core nor its own module
 * declares — it would vanish whenever the module that did declare it was
 * removed.
 *
 * Within a section, core's items come first, then each module's in `MODULES`
 * order. Each inherits its page's `anyOf`, so the entry and the route guard
 * are one declaration. Called with `useModules()` — this file never imports
 * the list.
 */
export function navigationWith(modules: readonly FrontendModule[]): readonly NavSection[] {
  const sections = new Map<string, NavSection>(
    NAVIGATION.map((section) => [section.id, { ...section, items: [...section.items] }]),
  );
  const core = new Set<string>(NAVIGATION.map((section) => section.id));

  for (const module of modules) {
    for (const declared of module.sections ?? []) {
      if (core.has(declared.id)) {
        throw new Error(`Module "${module.key}" declares the section "${declared.id}", which is core's.`);
      }
      if (!sections.has(declared.id)) sections.set(declared.id, { ...declared, items: [] });
    }
  }

  for (const module of modules) {
    const own = new Set<string>((module.sections ?? []).map((declared) => declared.id));
    for (const page of module.pages ?? []) {
      if (!page.nav) continue;
      const section = sections.get(page.nav.section);
      if (!section || (!core.has(page.nav.section) && !own.has(page.nav.section))) {
        throw new Error(
          `Module "${module.key}" puts "/${page.path}" in the section "${page.nav.section}", which neither core nor the module declares.`,
        );
      }
      section.items.push({
        labelKey: page.nav.labelKey,
        to: `/${page.path}`,
        icon: page.nav.icon,
        ...(page.anyOf ? { anyOf: page.anyOf } : {}),
        ...(page.nav.exact ? { exact: true } : {}),
      });
    }
  }

  // `sort` is stable, so sections with the same order keep core's, then declaration, order.
  return [...sections.values()].sort((a, b) => a.order - b.order);
}
